import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Save, Eye, Archive } from 'lucide-react';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import {
  getJobOfferById,
  createJobOffer,
  updateJobOffer,
  archiveJobOffer,
  generateSlug,
} from '../../../lib/offres';
import type { JobOfferInput, JobOfferStatus } from '../../../lib/offres';
import type { JobOfferContractType, JobOfferRemoteType } from '../../../types/offres';
import { CONTRACT_TYPE_LABELS, REMOTE_TYPE_LABELS } from '../../../types/offres';
import toast from 'react-hot-toast';

type ParcoursOption = {
  id: string;
  company_name: string;
  slug: string;
  status: string;
};

const EMPTY_FORM: JobOfferInput = {
  title: '',
  slug: '',
  contract_type: 'cdi',
  location_city: '',
  remote_type: 'no',
  description: '',
  profile: '',
  status: 'draft',
  internal_reference: '',
  sector: '',
  salary_range: '',
  salary_min: null,
  salary_max: null,
  salary_currency: 'EUR',
  published_at: null,
  valid_through: null,
  featured: false,
  image_url: '',
  company_name: '',
  company_logo_url: '',
  meta_title: '',
  meta_description: '',
  hiring_organization_name: '',
  hiring_organization_logo: '',
  hiring_organization_url: '',
  job_location_street_address: '',
  job_location_postal_code: '',
  job_location_country: 'FR',
  parcours_id: null,
};

function nullify(input: JobOfferInput): JobOfferInput {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    out[k] = v === '' ? null : v;
  }
  return out as JobOfferInput;
}

export default function EditOffreAdmin() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState<JobOfferInput>(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [parcoursOptions, setParcoursOptions] = useState<ParcoursOption[]>([]);

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }
      const { data } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
      if (data?.role !== 'admin' && data?.role !== 'editor') { navigate('/'); return; }

      if (!isNew && id) {
        try {
          const offer = await getJobOfferById(id);
          if (!offer) { navigate('/admin/offres'); return; }
          setForm({
            title: offer.title,
            slug: offer.slug,
            contract_type: offer.contract_type,
            location_city: offer.location_city,
            remote_type: offer.remote_type,
            description: offer.description,
            profile: offer.profile,
            status: offer.status,
            internal_reference: offer.internal_reference ?? '',
            sector: offer.sector ?? '',
            salary_range: offer.salary_range ?? '',
            salary_min: offer.salary_min,
            salary_max: offer.salary_max,
            salary_currency: offer.salary_currency ?? 'EUR',
            published_at: offer.published_at,
            valid_through: offer.valid_through,
            featured: offer.featured,
            image_url: offer.image_url ?? '',
            company_name: offer.company_name ?? '',
            company_logo_url: offer.company_logo_url ?? '',
            meta_title: offer.meta_title ?? '',
            meta_description: offer.meta_description ?? '',
            hiring_organization_name: offer.hiring_organization_name ?? '',
            hiring_organization_logo: offer.hiring_organization_logo ?? '',
            hiring_organization_url: offer.hiring_organization_url ?? '',
            job_location_street_address: offer.job_location_street_address ?? '',
            job_location_postal_code: offer.job_location_postal_code ?? '',
            job_location_country: offer.job_location_country ?? 'FR',
            parcours_id: offer.parcours_id ?? null,
          });
          setSlugManuallyEdited(true);
        } catch {
          toast.error('Offre introuvable.');
          navigate('/admin/offres');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }

      // Charger la liste des parcours pour le select
      const { data: parcoursList } = await supabase
        .from('parcours')
        .select('id, company_name, slug, status')
        .order('company_name', { ascending: true });
      if (parcoursList) setParcoursOptions(parcoursList as ParcoursOption[]);
    }
    init();
    // user?.id (et pas user) pour ne pas re-fetch quand Supabase recrée
    // l'objet user à chaque token refresh / changement de visibilité d'onglet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, id, isNew]);

  function set(field: keyof JobOfferInput, value: unknown) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleTitleChange(value: string) {
    set('title', value);
    if (!slugManuallyEdited) {
      set('slug', generateSlug(value));
    }
  }

  async function handleSave(targetStatus?: JobOfferStatus) {
    const finalForm = nullify({
      ...form,
      status: targetStatus ?? form.status,
    });

    if (!finalForm.title?.trim()) { toast.error('Le titre est obligatoire.'); return; }
    if (!finalForm.slug?.trim()) { toast.error('Le slug est obligatoire.'); return; }
    if (!finalForm.location_city?.trim()) { toast.error('La ville est obligatoire.'); return; }
    if (!finalForm.description?.trim()) { toast.error('La description est obligatoire.'); return; }
    if (!finalForm.profile?.trim()) { toast.error('Le profil est obligatoire.'); return; }

    const isPublishing = finalForm.status === 'published';

    setSaving(true);
    try {
      if (isNew) {
        await createJobOffer(finalForm);
        toast.success('Offre créée !');
      } else {
        await updateJobOffer(id!, finalForm);
        toast.success('Offre mise à jour !');
      }
      if (isPublishing) {
        navigate('/offres');
        return;
      }
      navigate('/admin/offres');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la sauvegarde.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!id || isNew) return;
    if (!confirm('Archiver cette offre ?')) return;
    try {
      await archiveJobOffer(id);
      toast.success('Offre archivée.');
      navigate('/admin/offres');
    } catch {
      toast.error('Impossible d\'archiver.');
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="mx-auto max-w-5xl px-6 py-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/offres" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {isNew ? 'Nouvelle offre' : 'Modifier l\'offre'}
              </h1>
              {!isNew && (
                <p className="text-xs text-slate-500 mt-0.5">/{form.slug}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isNew && form.status !== 'archived' && (
              <button
                onClick={handleArchive}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Archive className="h-4 w-4" /> Archiver
              </button>
            )}
            {!isNew && form.status === 'published' && (
              <a
                href={`/offres/${form.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Eye className="h-4 w-4" /> Voir
              </a>
            )}
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> Brouillon
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Publier'}
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_320px]">

          {/* Left column */}
          <div className="space-y-6">

            {/* Section 1 — Informations générales */}
            <Section title="Informations générales">
              <Field label="Titre *">
                <input
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="ex : Chargé(e) de recrutement"
                  className={inputClass}
                />
              </Field>
              <Field label="Slug *" hint="Généré automatiquement, modifiable">
                <input
                  value={form.slug}
                  onChange={(e) => { set('slug', e.target.value); setSlugManuallyEdited(true); }}
                  placeholder="charge-de-recrutement"
                  className={inputClass}
                />
              </Field>
              <Field label="Référence interne">
                <input
                  value={form.internal_reference ?? ''}
                  onChange={(e) => set('internal_reference', e.target.value)}
                  placeholder="ex : REC-2026-01"
                  className={inputClass}
                />
              </Field>
            </Section>

            {/* Section 2 — Poste */}
            <Section title="Poste">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Type de contrat *">
                  <select
                    value={form.contract_type}
                    onChange={(e) => set('contract_type', e.target.value as JobOfferContractType)}
                    className={inputClass}
                  >
                    {Object.entries(CONTRACT_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Télétravail *">
                  <select
                    value={form.remote_type}
                    onChange={(e) => set('remote_type', e.target.value as JobOfferRemoteType)}
                    className={inputClass}
                  >
                    {Object.entries(REMOTE_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Ville *">
                  <input
                    value={form.location_city}
                    onChange={(e) => set('location_city', e.target.value)}
                    placeholder="ex : Marseille"
                    className={inputClass}
                  />
                </Field>
                <Field label="Secteur">
                  <input
                    value={form.sector ?? ''}
                    onChange={(e) => set('sector', e.target.value)}
                    placeholder="ex : IT, Finance, RH..."
                    className={inputClass}
                  />
                </Field>
              </div>
              <Field label="Rémunération (texte libre)">
                <input
                  value={form.salary_range ?? ''}
                  onChange={(e) => set('salary_range', e.target.value)}
                  placeholder="ex : 35-45K€ selon profil"
                  className={inputClass}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Salaire min (€)">
                  <input
                    type="number"
                    value={form.salary_min ?? ''}
                    onChange={(e) => set('salary_min', e.target.value ? Number(e.target.value) : null)}
                    placeholder="35000"
                    className={inputClass}
                  />
                </Field>
                <Field label="Salaire max (€)">
                  <input
                    type="number"
                    value={form.salary_max ?? ''}
                    onChange={(e) => set('salary_max', e.target.value ? Number(e.target.value) : null)}
                    placeholder="45000"
                    className={inputClass}
                  />
                </Field>
                <Field label="Code postal">
                  <input
                    value={form.job_location_postal_code ?? ''}
                    onChange={(e) => set('job_location_postal_code', e.target.value)}
                    placeholder="13001"
                    className={inputClass}
                  />
                </Field>
              </div>
            </Section>

            {/* Section 3 — Description */}
            <Section title="Description">
              <Field label="Description du poste *" hint="Markdown supporté">
                <textarea
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={10}
                  placeholder="Décrivez le poste, les missions, le contexte..."
                  className={`${inputClass} resize-y`}
                />
              </Field>
              <Field label="Profil recherché *" hint="Markdown supporté">
                <textarea
                  value={form.profile}
                  onChange={(e) => set('profile', e.target.value)}
                  rows={8}
                  placeholder="Compétences, expérience, formation attendues..."
                  className={`${inputClass} resize-y`}
                />
              </Field>
            </Section>

            {/* Section 4 — Entreprise */}
            <Section title="Entreprise">
              <Field label="Parcours associé" hint="Relie cette offre à un parcours de découverte entreprise">
                <select
                  value={form.parcours_id ?? ''}
                  onChange={(e) => set('parcours_id', e.target.value || null)}
                  className={inputClass}
                >
                  <option value="">Aucun parcours</option>
                  {parcoursOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.company_name} — /decouvrir/{p.slug} — {p.status}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nom de l'entreprise">
                  <input
                    value={form.company_name ?? ''}
                    onChange={(e) => set('company_name', e.target.value)}
                    placeholder="recruteurs.io"
                    className={inputClass}
                  />
                </Field>
                <Field label="Nom pour schéma SEO">
                  <input
                    value={form.hiring_organization_name ?? ''}
                    onChange={(e) => set('hiring_organization_name', e.target.value)}
                    placeholder="recruteurs.io"
                    className={inputClass}
                  />
                </Field>
                <Field label="URL logo">
                  <input
                    value={form.company_logo_url ?? ''}
                    onChange={(e) => set('company_logo_url', e.target.value)}
                    placeholder="https://..."
                    className={inputClass}
                  />
                </Field>
                <Field label="Site entreprise">
                  <input
                    value={form.hiring_organization_url ?? ''}
                    onChange={(e) => set('hiring_organization_url', e.target.value)}
                    placeholder="https://recruteurs.io"
                    className={inputClass}
                  />
                </Field>
              </div>
            </Section>

            {/* Section 6 — SEO */}
            <Section title="SEO">
              <Field label="Meta titre" hint="Affiché dans Google si renseigné">
                <input
                  value={form.meta_title ?? ''}
                  onChange={(e) => set('meta_title', e.target.value)}
                  placeholder={`${form.title || 'Titre du poste'} — recruteurs.io`}
                  className={inputClass}
                  maxLength={70}
                />
              </Field>
              <Field label="Meta description">
                <textarea
                  value={form.meta_description ?? ''}
                  onChange={(e) => set('meta_description', e.target.value)}
                  rows={3}
                  placeholder="Description courte pour les moteurs de recherche..."
                  className={`${inputClass} resize-none`}
                  maxLength={160}
                />
              </Field>
            </Section>

          </div>

          {/* Right column — Publication */}
          <div className="space-y-5">

            <Section title="Publication">
              <Field label="Statut">
                <select
                  value={form.status}
                  onChange={(e) => set('status', e.target.value as JobOfferStatus)}
                  className={inputClass}
                >
                  <option value="draft">Brouillon</option>
                  <option value="published">Publiée</option>
                  <option value="archived">Archivée</option>
                </select>
              </Field>
              <Field label="Valide jusqu'au">
                <input
                  type="date"
                  value={form.valid_through ? form.valid_through.split('T')[0] : ''}
                  onChange={(e) => set('valid_through', e.target.value ? `${e.target.value}T23:59:00.000Z` : null)}
                  className={inputClass}
                />
              </Field>
              <div className="flex items-center gap-3 pt-1">
                <input
                  type="checkbox"
                  id="featured"
                  checked={form.featured ?? false}
                  onChange={(e) => set('featured', e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-pink-600"
                />
                <label htmlFor="featured" className="text-sm font-medium text-slate-700">
                  Mettre en avant
                </label>
              </div>
            </Section>

          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-5 text-base font-bold text-slate-900">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {hint && <span className="ml-1.5 text-xs font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-slate-900 focus:ring-1 focus:ring-slate-900';
